/**
 * PauseBanner
 * ===========
 * Sticky banner shown while a pause session is active.
 * Displays live timer + ArbZG compliance hint + end button.
 *
 * UX-Hardening (29.04.2026):
 *  - Nudge-Stufe nach 60 Min: "Pause laeuft seit X — beenden?"
 *  - Nudge-Stufe nach 120 Min: roter Alarm "Pause sehr lange — bitte pruefen!"
 */
import { useMemo } from 'react';
import { Coffee, CheckCircle2, AlertTriangle, BellRing } from 'lucide-react';
import { usePause } from '@/context/PauseContext';

const fmt = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const fmtHumanDuration = (secs) => {
  const totalMin = Math.floor(secs / 60);
  if (totalMin < 60) return `${totalMin} Min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h} Std ${m} Min` : `${h} Std`;
};

const REASON_LABEL = {
  statutory: 'Gesetzliche Pause (ArbZG §4)',
  operational: 'Pause · Warten / Material',
  other: 'Pause · Sonstiges',
};

const PauseBanner = () => {
  const { activeSession, elapsedSeconds, endPause, loading } = usePause();
  
  const arbzgState = useMemo(() => {
    if (!activeSession || activeSession.reason_type !== 'statutory') return null;
    const mins = Math.floor(elapsedSeconds / 60);
    if (mins < 15) return { level: 'below_min', text: `Unter 15 Min · ArbZG-Stueckelung (min. 15 Min)` };
    if (mins < 30) return { level: 'near', text: `${30 - mins} Min bis ArbZG-Minimum 30 Min` };
    if (mins < 45) return { level: 'met_30', text: `ArbZG-Minimum 30 Min erfuellt` };
    return { level: 'met_45', text: `ArbZG-Minimum 45 Min erfuellt (>9h Arbeit)` };
  }, [activeSession, elapsedSeconds]);
  
  // Nudge-Stufen (verhindert "Vergessen-Auszuloggen-mit-laufender-Pause"-Bug)
  const nudge = useMemo(() => {
    if (!activeSession) return null;
    const mins = Math.floor(elapsedSeconds / 60);
    if (mins >= 120) {
      return {
        level: 'critical',
        text: `Pause laeuft seit ${fmtHumanDuration(elapsedSeconds)} — bitte pruefen!`,
      };
    }
    if (mins >= 60) {
      return {
        level: 'warn',
        text: `Pause laeuft seit ${fmtHumanDuration(elapsedSeconds)} — noch korrekt?`,
      };
    }
    return null;
  }, [activeSession, elapsedSeconds]);
  
  if (!activeSession) return null;
  
  const reasonLabel = REASON_LABEL[activeSession.reason_type] || 'Pause';
  // Banner-Farbe wechselt bei langen Pausen — kritischer Zustand wird auffaellig rot
  const bannerCls = nudge?.level === 'critical'
    ? 'bg-gradient-to-r from-red-600 to-rose-600 text-white animate-pulse-slow'
    : nudge?.level === 'warn'
      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
      : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white';
  
  return (
    <div
      role="status"
      data-testid="pause-banner"
      className={`sticky top-0 z-[60] w-full shadow-md ${bannerCls}`}
    >
      <div className="max-w-7xl mx-auto flex items-center gap-3 px-4 py-2">
        <Coffee className="w-5 h-5 shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <span className="truncate">{reasonLabel}</span>
            <span className="ml-auto tabular-nums font-mono" data-testid="pause-banner-timer">{fmt(elapsedSeconds)}</span>
          </div>
          {arbzgState && !nudge && (
            <div className="flex items-center gap-1 text-[11px] opacity-90 mt-0.5">
              {arbzgState.level === 'below_min' && <AlertTriangle className="w-3 h-3 shrink-0" />}
              {(arbzgState.level === 'met_30' || arbzgState.level === 'met_45') && <CheckCircle2 className="w-3 h-3 shrink-0" />}
              <span className="truncate">{arbzgState.text}</span>
            </div>
          )}
          {nudge && (
            <div
              className="flex items-center gap-1 text-[11px] font-medium mt-0.5"
              data-testid={`pause-banner-nudge-${nudge.level}`}
            >
              <BellRing className="w-3 h-3 shrink-0 animate-pulse" />
              <span className="truncate">{nudge.text}</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => endPause()}
          disabled={loading}
          className={
            'shrink-0 h-8 rounded-md disabled:opacity-60 px-3 text-xs font-semibold transition-colors ' +
            (nudge?.level === 'critical'
              ? 'bg-white text-red-700 hover:bg-white/90 ring-2 ring-white/40'
              : 'bg-white/15 hover:bg-white/25')
          }
          data-testid="pause-banner-end"
        >
          {nudge?.level === 'critical' ? 'Jetzt beenden' : 'Beenden'}
        </button>
      </div>
    </div>
  );
};

export default PauseBanner;
